"use client"

import { useState, useRef } from "react"
import axios from "axios"
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  Grid,
} from "@mui/material"
import { CSVLink } from "react-csv"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import DeleteIcon from "@mui/icons-material/Delete"

function InvoiceViewer() {
  // State for file upload
  const [selectedFiles, setSelectedFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const fileInputRef = useRef(null)
  const [totalUnits,setTotalUnits] = useState(0)

  // State for invoice data
  const [invoiceData, setInvoiceData] = useState(null)
  const [combinedInvoiceItems, setCombinedInvoiceItems] = useState([])

  // State for table pagination
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // File upload handlers
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files)
    const validFiles = files.filter((file) => file.type === "application/pdf")

    if (validFiles.length !== files.length) {
      alert("Only PDF files are allowed")
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles])
    }
  }

  const handleDragOver = (event) => {
    event.preventDefault()
  }

  const handleDrop = (event) => {
    event.preventDefault()
    const files = Array.from(event.dataTransfer.files)
    const validFiles = files.filter((file) => file.type === "application/pdf")

    if (validFiles.length !== files.length) {
      alert("Only PDF files are allowed")
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles])
    }
  }

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return

    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const formData = new FormData()

      // Add all selected files to the formData with the field name 'files'
      selectedFiles.forEach((file) => {
        formData.append("files", file)
      })

      // Make the API call using axios
      const response = await axios.post("https://ai.buyume.io/invoice/extract-multiple-invoices/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      // Axios automatically parses JSON response
      const data = response.data
      setInvoiceData(data)
      setSuccess("Invoice data extracted successfully!")
      let tempUnits = 0
      // Combine all invoice data into a single array
      const allInvoiceItems = data.results.reduce((acc, result) => {
        // Add a source field to identify which invoice this item came from
        const itemsWithSource = result.invoice_data.map((item) => {
          tempUnits+=Number(item?.Units)||0
          return {
           ...item,
            Source: result.filename,
          }
        })
        return [...acc, ...itemsWithSource]
      }, [])
      setTotalUnits(tempUnits)
      setCombinedInvoiceItems(allInvoiceItems)
    } catch (err) {
      console.error("Error uploading files:", err)
      alert('Someting went wrong')
    } finally {
      setLoading(false)
    }
  }

  // Table handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Generate dynamic headers for CSV export
  const generateCsvHeaders = () => {
    if (!combinedInvoiceItems.length) return []

    // Create headers from the keys in the first item
    return Object.keys(combinedInvoiceItems[0]).map((key) => ({
      key: key,
      label: key, // You can transform this to a more readable format if needed
    }))
  }

  // Render the file uploader section - optimized for less space
  const renderFileUploader = () => {
    return (
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Left side - Upload area */}
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                backgroundColor: "#f8f9fa",
                border: "1px dashed #ccc",
                borderRadius: 1,
                height: "100%",
                minHeight: "100px",
              }}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
            >
              <CloudUploadIcon sx={{ fontSize: 32, color: "primary.main", mb: 1 }} />
              <Typography variant="body2" align="center">
                Drag PDFs here or click to select
              </Typography>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                ref={fileInputRef}
                style={{ display: "none" }}
                multiple
              />
            </Paper>
          </Grid>

          {/* Right side - Selected files and upload button */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
              {/* Selected files */}
              <Box sx={{ mb: 1, flexGrow: 1, overflow: "auto", maxHeight: "100px" }}>
                {selectedFiles.length > 0 ? (
                  <>
                    <Typography variant="caption" display="block" gutterBottom>
                      Selected Files ({selectedFiles.length}):
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selectedFiles.map((file, index) => (
                        <Chip
                          key={index}
                          label={file.name}
                          onDelete={() => handleRemoveFile(index)}
                          deleteIcon={<DeleteIcon fontSize="small" />}
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No files selected
                  </Typography>
                )}
              </Box>

              {/* Upload button */}
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={selectedFiles.length === 0 || loading}
                size="small"
                fullWidth
              >
                {loading ? <CircularProgress size={20} /> : "Process Invoices"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    )
  }

  

  // Render the invoice table section
  const renderInvoiceTable = () => {
    if (!combinedInvoiceItems.length) {
      return null
    }

    // Generate dynamic headers for CSV
    const csvHeaders = generateCsvHeaders()

    return (
      <Paper elevation={2}>
        <Box sx={{ p: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="subtitle1">Invoice Data ({combinedInvoiceItems.length} items)</Typography>

          {/* Custom styled CSV download link */}
          <Typography>Total Units : {totalUnits}</Typography>
          <CSVLink
            data={combinedInvoiceItems}
            headers={csvHeaders}
            filename="Invoice_Data_Extract.csv"
            title="Invoice Data Extract"
            style={{
              textDecoration: "none",
              border: "1px solid #999",
              padding: "10px 20px",
              backgroundColor: "#95c7f1",
              marginTop: 15,
              display: "block",
              justifyContent: "center",
              alignItems: "center",
              width: 120,
              marginLeft: 20,
              textAlign: "center",
              borderRadius: "4px",
              color: "#000",
              fontWeight: "500",
              fontSize: "14px",
            }}
          >
            Download
          </CSVLink>
        </Box>

        <TableContainer sx={{ maxHeight: "calc(100vh - 250px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {combinedInvoiceItems.length > 0 &&
                  Object.keys(combinedInvoiceItems[0]).map((header, index) => (
                    <TableCell key={index} sx={{ fontWeight: "bold", py: 1 }}>
                      {header}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {combinedInvoiceItems
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, rowIndex) => (
                  <TableRow key={rowIndex} hover>
                    {Object.values(item).map((value, cellIndex) => (
                      <TableCell key={cellIndex} sx={{ py: 1 }}>
                        {value}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={combinedInvoiceItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: "1px solid rgba(224, 224, 224, 1)" }}
        />
      </Paper>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Invoice Data Extractor
      </Typography>

      {renderFileUploader()}
      {renderInvoiceTable()}
    </Container>
  )
}

export default InvoiceViewer
